import React from 'react';
import './Stats.css';

const EMOTION_EMOJI = {
  happy: '😊', neutral: '😐', sad: '😔',
  angry: '😠', fearful: '😨', disgusted: '😒', surprised: '😲',
};

const EMOTION_COLOR = {
  happy: '#4ade80', neutral: '#94a3b8', sad: '#60a5fa',
  angry: '#ef4444', fearful: '#f97316', disgusted: '#a78bfa', surprised: '#fbbf24',
};

function getColor(val, lo, hi, inverse = false) {
  if (inverse) return val <= lo ? '#4ade80' : val <= hi ? '#fbbf24' : '#ef4444';
  return val >= hi ? '#4ade80' : val >= lo ? '#fbbf24' : '#ef4444';
}

function Bar({ pct, color }) {
  return (
    <div className="bar-track">
      <div className="bar-fill" style={{ width: `${Math.min(100, Math.max(0, pct))}%`, background: color }} />
    </div>
  );
}

function Stats({ stats, isRunning, emotionHistory = [], liveWpm = 0, liveFillerPct = -1 }) {
  const DECAY_START = 5.0;   // seconds of silence before WPM starts falling
  const DECAY_OVER  = 2.0;   // seconds it takes to reach 0 after decay starts
  const baseWpm     = liveWpm > 0 ? liveWpm : stats.wpm;
  const silence     = stats.silence ?? 0;
  const decay       = silence >= DECAY_START
    ? Math.max(0, 1 - (silence - DECAY_START) / DECAY_OVER)
    : 1;
  const displayWpm  = Math.round(baseWpm * decay);
  const fillerPct   = liveFillerPct >= 0 ? liveFillerPct : stats.fillerWords;
  const eyeColor  = getColor(stats.eyeContact, 50, 70);
  const nervColor = getColor(stats.nervousness, 30, 60, true);
  const fillColor = getColor(fillerPct, 10, 20, true);
  const wpmOk     = displayWpm >= 100 && displayWpm <= 160;
  const wpmColor  = wpmOk ? '#4ade80' : displayWpm > 0 ? '#fbbf24' : '#666';
  const confPct   = stats.confidence ? Math.round(stats.confidence * 100) : null;

  return (
    <div className="stats-panel">
      <div className="stats-header">
        <h3>live stats</h3>
        {isRunning && <span className="live-pill">● live</span>}
      </div>

      {/* Emotion row with confidence + sparkline */}
      <div className="stat-row">
        <div className="stat-label-row">
          <span className="stat-label">emotion</span>
          {confPct !== null && isRunning && (
            <span className="emotion-confidence">CNN {confPct}%</span>
          )}
        </div>
        <div className="emotion-row">
          <span className="stat-emotion">
            {EMOTION_EMOJI[stats.emotion] || '😐'} {stats.emotion}
          </span>
          {emotionHistory.length > 0 && (
            <div className="emotion-sparkline" title="emotion history (oldest → newest)">
              {emotionHistory.map((e, i) => (
                <div
                  key={i}
                  className="spark-dot"
                  style={{ background: EMOTION_COLOR[e.emotion] || '#444', opacity: 0.4 + 0.6 * (i / emotionHistory.length) }}
                  title={e.emotion}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Eye contact */}
      <div className="stat-row">
        <div className="stat-label-row">
          <span className="stat-label">eye contact</span>
          <span className="stat-num" style={{ color: eyeColor }}>{stats.eyeContact}%</span>
        </div>
        <Bar pct={stats.eyeContact} color={eyeColor} />
      </div>

      {/* Speech rate */}
      <div className="stat-row">
        <div className="stat-label-row">
          <span className="stat-label">speech rate</span>
          <span className="stat-num" style={{ color: wpmColor }}>
            {displayWpm > 0 ? `${displayWpm} wpm` : '— wpm'}
          </span>
        </div>
        <div className="bar-track">
          <div className="bar-fill" style={{ width: `${Math.min(100, (displayWpm / 260) * 100)}%`, background: wpmColor }} />
          <div className="wpm-zone" />
        </div>
        <span className="bar-hint">ideal: 100–160 wpm</span>
      </div>

      {/* Composure */}
      <div className="stat-row">
        <div className="stat-label-row">
          <span className="stat-label">composure</span>
          <span className="stat-num" style={{ color: nervColor }}>{100 - stats.nervousness}%</span>
        </div>
        <Bar pct={100 - stats.nervousness} color={nervColor} />
      </div>

      {/* Filler words */}
      <div className="stat-row">
        <div className="stat-label-row">
          <span className="stat-label">filler words</span>
          <span className="stat-num" style={{ color: fillColor }}>{fillerPct}%</span>
        </div>
        <Bar pct={Math.max(0, 100 - fillerPct * 5)} color={fillColor} />
      </div>

      {!isRunning && (
        <p className="stats-hint">start an interview to see live stats</p>
      )}
    </div>
  );
}

export default Stats;
